<template>
  <div>
    <b-row>
      <b-col cols="12" md="4" lg="5" class="mb-3">
        <date-picker
          v-model="dateRange"
          type="date"
          range
          format="DD MMM YY"
          :shortcuts="dateRangeShortcuts"
          :disabled-date="notAfterToday"
          placeholder="เลือกวันที่ต้องการค้นหา"
          input-class="form-control"
          :class="{ 'is-invalid': dateRange.length === 2 && !isDateRangeValid }"
        >
        </date-picker>
        <div v-if="!isDateRangeValid" class="invalid-feedback d-block">
          {{ dateRangeErrorMessage }}
        </div>
      </b-col>
      <b-col cols="12" md="4" lg="4" class="">
        <b-form-group
          label="ดูรายงานตาม:"
          label-cols="4"
          label-align-sm="right"
        >
          <b-form-select
            class="col"
            v-model="groupBy"
            :options="options"
          ></b-form-select>
        </b-form-group>
      </b-col>

      <b-col cols="12" md="4" lg="3" class="mb-3">
        <b-button
          class="float-right mb-2 ml-2"
          variant="primary"
          :disabled="!dateRange || dateRange.length !== 2 || !isDateRangeValid"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
        <b-button
          class="float-right ml-2"
          variant="secondary"
          :disabled="
            isSendingEmail ||
            cooldownRemaining > 0 ||
            !dateRange ||
            dateRange.length !== 2 ||
            !isDateRangeValid
          "
          @click="sendEmail"
        >
          Email
        </b-button>
        <b-button
          class="float-right"
          variant="info"
          :disabled="reportList.length === 0"
          @click="excelExport"
          ><i class="fas fa-file-excel"></i
        ></b-button>
      </b-col>
    </b-row>

    <!-- Tables per Doctor -->
    <div v-for="doctor in reportList" :key="doctor.doctorUid" class="mb-4">
      <h5 class="mt-3 mb-2">
        {{ doctor.doctorName }}
        <span class="text-muted">(รวม DF: {{ formatNumber(doctor.totalDf) }})</span>
      </h5>
      <b-table
        :id="'DfTable-' + doctor.doctorUid"
        :ref="'DfTable-' + doctor.doctorUid"
        small
        sort-icon-left
        head-variant="light"
        :busy="tableBusy"
        :items="formatDoctorRows(doctor.dates)"
        :fields="tableFields"
        :responsive="true"
        show-empty
      >
        <template #cell(date)="row">{{ row.value }}</template>
        <template #cell(df)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(feePercent)="row">{{ row.value }}%</template>
        <template #cell(feeAmount)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(netDf)="row">{{ formatNumber(row.value) }}</template>

        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td class="text-left">รวม</td>
            <td></td>
            <td>{{ formatNumber(calculateDoctorTotal(doctor.dates, 'df')) }}</td>
            <td></td>
            <td>{{ formatNumber(calculateDoctorTotal(doctor.dates, 'feeAmount')) }}</td>
            <td>{{ formatNumber(calculateDoctorTotal(doctor.dates, 'netDf')) }}</td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <!-- Summary Section -->
    <b-card v-if="summary.totalDf" class="mt-4" no-body>
      <b-card-header>
        <strong>สรุปรวม</strong>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col md="6" class="mb-3">
            <h6>สรุปตามแพทย์</h6>
            <b-table
              id="DfSummaryByDoctor"
              :items="summaryByDoctorWithFee"
              :fields="doctorSummaryFields"
              small
              head-variant="light"
              show-empty
            >
              <template #cell(df)="row">{{ formatNumber(row.value) }}</template>
              <template #cell(feeAmount)="row">{{ formatNumber(row.value) }}</template>
              <template #cell(netDf)="row">{{ formatNumber(row.value) }}</template>
            </b-table>
          </b-col>
          <b-col md="6" class="mb-3">
            <h6>สรุปตามวิธีชำระ</h6>
            <b-table
              id="DfSummaryByPayment"
              :items="summaryByPaymentWithFee"
              :fields="paymentSummaryFields"
              small
              head-variant="light"
              show-empty
            >
              <template #cell(df)="row">{{ formatNumber(row.value) }}</template>
              <template #cell(feePercent)="row">{{ row.value }}%</template>
              <template #cell(feeAmount)="row">{{ formatNumber(row.value) }}</template>
              <template #cell(netDf)="row">{{ formatNumber(row.value) }}</template>
            </b-table>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col class="text-right">
            <p class="mb-1">รวม DF ทั้งหมด: <strong>{{ formatNumber(totalSummary.df) }}</strong></p>
            <p class="mb-1">รวมค่าธรรมเนียม: <strong>{{ formatNumber(totalSummary.feeAmount) }}</strong></p>
            <p class="mb-0 font-weight-bold">รวม DF สุทธิ: <strong>{{ formatNumber(totalSummary.netDf) }}</strong></p>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";

import XLSX from "xlsx";

export default {
  name: "DfByPaymentTypeReport",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,

      dateRange: [],
      groupBy: "day",
      options: [
        { value: "day", text: "วัน" },
        { value: "month", text: "เดือน" },
        { value: "year", text: "ปี" },
      ],

      //table
      tableBusy: false,

      reportList: [],
      rawTableHeader: [],
      headerName: "วัน/เดือน/ปี",
      summary: {},

      isSendingEmail: false,
      cooldownRemaining: 0,
      cooldownTimer: null,
      cooldownPeriod: 60,

      tableFields: [
        { key: "date", label: "วัน/เดือน/ปี" },
        { key: "paymentName", label: "วิธีชำระ" },
        { key: "df", label: "DF", class: "text-right" },
        { key: "feePercent", label: "ค่าธรรมเนียม %", class: "text-right" },
        { key: "feeAmount", label: "ค่าธรรมเนียม", class: "text-right" },
        { key: "netDf", label: "DF สุทธิ", class: "text-right font-weight-bold" },
      ],

      doctorSummaryFields: [
        { key: "doctorName", label: "แพทย์" },
        { key: "df", label: "DF", class: "text-right" },
        { key: "feeAmount", label: "ค่าธรรมเนียม", class: "text-right" },
        { key: "netDf", label: "DF สุทธิ", class: "text-right font-weight-bold" },
      ],
      paymentSummaryFields: [
        { key: "name", label: "วิธีชำระ" },
        { key: "df", label: "DF", class: "text-right" },
        { key: "feePercent", label: "ค่าธรรมเนียม %", class: "text-right" },
        { key: "feeAmount", label: "ค่าธรรมเนียม", class: "text-right" },
        { key: "netDf", label: "DF สุทธิ", class: "text-right font-weight-bold" },
      ],
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      paymentMethodList: "moduleUser/getPaymentMethodList",
    }),

    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    emailLastSentTime: {
      get() {
        const savedTime = localStorage.getItem("dfByPaymentEmailLastSentTime");
        return savedTime ? parseInt(savedTime) : null;
      },
      set(value) {
        if (value) {
          localStorage.setItem("dfByPaymentEmailLastSentTime", value.toString());
        } else {
          localStorage.removeItem("dfByPaymentEmailLastSentTime");
        }
      },
    },
    isDateRangeValid() {
      if (!this.dateRange || this.dateRange.length !== 2) return true;

      const startDate = moment(this.dateRange[0]);
      const endDate = moment(this.dateRange[1]);

      const diffDays = endDate.diff(startDate, "days");

      // Check max 31 days
      if (diffDays > 31) return false;

      // Check before cutoff or cross cutoff
      const cutoff = moment("2026-01-01").startOf("day");
      const start = startDate.startOf("day");
      const end = endDate.startOf("day");

      // Before cutoff
      if (end.isBefore(cutoff)) return false;

      // Cross cutoff
      if (start.isBefore(cutoff) && end.isSameOrAfter(cutoff)) return false;

      return true;
    },

    dateRangeErrorMessage() {
      if (!this.dateRange || this.dateRange.length !== 2) return "";

      const startDate = moment(this.dateRange[0]);
      const endDate = moment(this.dateRange[1]);
      const diffDays = endDate.diff(startDate, "days");

      if (diffDays > 31) return "ช่วงเวลาต้องไม่เกิน 1 เดือน";
      if (this.isBeforeCutoff) return "รายงานนี้ใช้ได้เฉพาะข้อมูลหลัง 1 ม.ค. 69";
      if (this.isCrossDateRange) return "ไม่สามารถ query ข้ามช่วง 1 ม.ค. 69 ได้";
      return "";
    },

    // Check if date range is before cutoff (1 Jan 2026)
    isBeforeCutoff() {
      if (!this.dateRange || this.dateRange.length !== 2) return false;
      const cutoff = moment("2026-01-01").startOf("day");
      return moment(this.dateRange[1]).startOf("day").isBefore(cutoff);
    },

    // Check if date range crosses cutoff
    isCrossDateRange() {
      if (!this.dateRange || this.dateRange.length !== 2) return false;
      const cutoff = moment("2026-01-01").startOf("day");
      const start = moment(this.dateRange[0]).startOf("day");
      const end = moment(this.dateRange[1]).startOf("day");
      return start.isBefore(cutoff) && end.isSameOrAfter(cutoff);
    },

    // Summary by doctor with fee calculation
    summaryByDoctorWithFee() {
      if (!this.reportList.length) return [];

      return this.reportList.map(doctor => {
        let totalDf = 0;
        let totalFee = 0;

        doctor.dates.forEach(date => {
          date.dfByPaymentType.forEach(pm => {
            const feePercent = this.getProcessingFee(pm.keyName);
            const feeAmount = (pm.value * feePercent) / 100;
            totalDf += pm.value;
            totalFee += feeAmount;
          });
        });

        return {
          doctorName: doctor.doctorName,
          df: totalDf,
          feeAmount: totalFee,
          netDf: totalDf - totalFee,
        };
      });
    },

    // Summary by payment type with fee calculation
    summaryByPaymentWithFee() {
      if (!this.summary.byPaymentType) return [];

      return this.summary.byPaymentType.map(pm => {
        const feePercent = this.getProcessingFeeByName(pm.name);
        const feeAmount = (pm.value * feePercent) / 100;

        return {
          name: pm.name,
          df: pm.value,
          feePercent: feePercent,
          feeAmount: feeAmount,
          netDf: pm.value - feeAmount,
        };
      });
    },

    // Total summary
    totalSummary() {
      let totalDf = 0;
      let totalFee = 0;

      this.summaryByPaymentWithFee.forEach(pm => {
        totalDf += pm.df;
        totalFee += pm.feeAmount;
      });

      return {
        df: totalDf,
        feeAmount: totalFee,
        netDf: totalDf - totalFee,
      };
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchDfByPaymentType: "moduleReport/fetchDfByPaymentType",
      emailDfByPaymentType: "moduleReport/emailDfByPaymentType",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
    }),
    init() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    moment() {
      return moment();
    },
    formatNumber(num) {
      if (num === null || num === undefined) return "-";
      return num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    displayDate(date) {
      if (this.groupBy === "year")
        return "ปี " + moment(date, "YYYY").locale("th").format("YYYY");
      if (this.groupBy === "month")
        return moment(date, "YYYY-MM").locale("th").format("MMM YY");
      return moment(date, "YYYY-MM-DD").locale("th").format("D MMM YY");
    },

    // Get processing fee by keyName
    getProcessingFee(keyName) {
      const method = this.paymentMethodList.find(m => m.keyName === keyName);
      return method?.processingFee || 0;
    },

    // Get processing fee by payment name
    getProcessingFeeByName(name) {
      const method = this.paymentMethodList.find(m => m.name === name);
      return method?.processingFee || 0;
    },

    // Format doctor dates into separate rows per payment type
    formatDoctorRows(dates) {
      const rows = [];

      dates.forEach(d => {
        d.dfByPaymentType.forEach(pm => {
          if (pm.value > 0) {
            const feePercent = this.getProcessingFee(pm.keyName);
            const feeAmount = (pm.value * feePercent) / 100;

            rows.push({
              date: this.displayDate(d.creationDt),
              paymentName: pm.name,
              keyName: pm.keyName,
              df: pm.value,
              feePercent: feePercent,
              feeAmount: feeAmount,
              netDf: pm.value - feeAmount,
            });
          }
        });
      });

      return rows;
    },

    // Calculate doctor total for a specific field
    calculateDoctorTotal(dates, field) {
      const rows = this.formatDoctorRows(dates);
      return rows.reduce((sum, row) => sum + (row[field] || 0), 0);
    },

    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },

    async sendEmail() {
      if (!this.validateDateRange()) return;

      if (this.dateFrom === "" || this.dateTo === "") {
        this.$refs.Dialog.showAlertInfo(
          "ข้อมูลไม่ครบถ้วน",
          "กรุณาเลือกช่วงวันที่ที่ต้องการก่อนส่งรายงาน",
          "warning"
        );
        return;
      }

      if (this.isSendingEmail || this.cooldownRemaining > 0) {
        return;
      }

      try {
        this.isSendingEmail = true;

        await this.emailDfByPaymentType({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          groupBy: this.groupBy,
        });

        this.emailLastSentTime = Date.now();

        this.$refs.Dialog.showAlertInfo(
          "การส่งรายงานเสร็จสมบูรณ์",
          "ระบบได้จัดส่งรายงานไปยังอีเมลที่ได้ลงทะเบียนไว้แล้ว กระบวนการอาจใช้เวลาประมาณ 5-15 นาที หากไม่พบอีเมลในกล่องจดหมายหลัก กรุณาตรวจสอบในโฟลเดอร์จดหมายขยะ (Junk Mail)",
          "success"
        );

        this.startCooldown(this.cooldownPeriod);
      } catch (error) {
        this.$refs.Dialog.showAlertInfo(
          "เกิดข้อผิดพลาด",
          "ไม่สามารถส่งรายงานได้ กรุณาลองใหม่อีกครั้ง",
          "error"
        );
        console.error("Error sending email report:", error);
      } finally {
        this.isSendingEmail = false;
      }
    },

    startCooldown(seconds) {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
      }

      this.cooldownRemaining = seconds;

      this.cooldownTimer = setInterval(() => {
        this.cooldownRemaining--;

        if (this.cooldownRemaining <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },
    fetchReport() {
      if (!this.validateDateRange()) return;

      if (this.dateFrom === "" || this.dateTo === "") return;

      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.fetchDfByPaymentType({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        groupBy: this.groupBy,
      })
        .then(res => {
          this.reportList = res.data.item || [];
          this.rawTableHeader = res.data.header?.receiptPayment || [];
          this.headerName = res.data.header?.name || "วัน/เดือน/ปี";
          this.summary = res.data.summary || {};
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    excelExport() {
      document.querySelectorAll(".sr-only").forEach(e => e.remove());

      let wb = XLSX.utils.book_new();
      let filename = "df-by-payment-type-report.xlsx";

      // Export each doctor's table
      this.reportList.forEach(doctor => {
        const tableId = "DfTable-" + doctor.doctorUid;
        const tbl = document.getElementById(tableId);
        if (tbl) {
          const ws = XLSX.utils.table_to_sheet(tbl);
          const sheetName = doctor.doctorName.substring(0, 31);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        }
      });

      // Export summary tables
      const summaryDoctorTbl = document.getElementById("DfSummaryByDoctor");
      if (summaryDoctorTbl) {
        const ws = XLSX.utils.table_to_sheet(summaryDoctorTbl);
        XLSX.utils.book_append_sheet(wb, ws, "สรุปตามแพทย์");
      }

      const summaryPaymentTbl = document.getElementById("DfSummaryByPayment");
      if (summaryPaymentTbl) {
        const ws = XLSX.utils.table_to_sheet(summaryPaymentTbl);
        XLSX.utils.book_append_sheet(wb, ws, "สรุปตามวิธีชำระ");
      }

      XLSX.writeFile(wb, filename);
    },
    notAfterToday(date) {
      if (date > new Date(new Date().setHours(0, 0, 0, 0))) {
        return true;
      }

      if (this.dateRange && this.dateRange.length === 1 && this.dateRange[0]) {
        const startDate = moment(this.dateRange[0]);
        const currentDate = moment(date);

        if (currentDate.isBefore(startDate)) {
          return false;
        }

        const diffDays = currentDate.diff(startDate, "days");
        return diffDays > 31;
      }

      return false;
    },
    validateDateRange() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$refs.Dialog.showAlertInfo(
          "ข้อมูลไม่ครบถ้วน",
          "กรุณาเลือกช่วงวันที่ที่ต้องการก่อนแสดงรายงาน",
          "warning"
        );
        return false;
      }

      if (!this.isDateRangeValid) {
        this.$refs.Dialog.showAlertInfo(
          "ช่วงวันที่ไม่ถูกต้อง",
          "กรุณาเลือกช่วงวันที่ไม่เกิน 1 เดือน",
          "warning"
        );
        return false;
      }

      return true;
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }

    const lastSentTime = this.emailLastSentTime;
    if (lastSentTime) {
      const elapsedSeconds = Math.floor((Date.now() - lastSentTime) / 1000);

      if (elapsedSeconds < this.cooldownPeriod) {
        this.startCooldown(this.cooldownPeriod - elapsedSeconds);
      }
    }
  },
  updated() {},
  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
  },
};
</script>

<style>
</style>
